import * as React from 'react'
import {Box, Typography, Tooltip} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

import {kpiLabelSx} from '../../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../../styles/foundation/typography'
import {NAVY, SUBTLE, MUTED, BORDER, LIGHT_BG} from '../../../../../styles/foundation/colors'
import {GAP} from '../../../../../styles/foundation/spacing'

import {volumetricKg, toTriple, getVolumetricDivisor} from '../../weightMath'
import {dims as fmtDims, int, kg} from '../../format'
import {volumetric} from '../state/packingModel'

/**
 * The volumetric arithmetic, shown rather than asserted.
 *
 * `L x B x H / RULES.VOLUMETRIC_DIVISOR = X kg`, rendered so a merchant can
 * see where the number came from instead of being handed a figure. The
 * divisor is printed from the constant, so the working on screen can never
 * disagree with the maths behind it.
 *
 * Three honest states, because dimensions have three honest conditions:
 *
 *  - a complete, positive triple renders the full working;
 *  - no dimensions at all (flyers, which take the shape of whatever goes in
 *    them) render the nil result in words — never `0 x 0 x 0`, which would
 *    read as a measurement of zero rather than the absence of a box;
 *  - a partly-filled or non-positive triple renders no figure, matching
 *    packing/state/packingModel.js's `volumetric`, which returns null for
 *    exactly that input. Showing 0.00 kg here while the table beside it
 *    shows an em dash is the drift this panel must not introduce.
 *
 * "Usable" is `packingModel.volumetric`'s own completeness gate, imported
 * rather than re-derived — the sibling `packing/components/CartonFigure.js`
 * imports the same function for the same purpose, so the two can never
 * disagree about what counts as a usable triple. The gate is `!= null`,
 * never falsy: `volumetric()` legitimately returns `0` for any carton under
 * 25 cm³, and a real `0` must still render the full working, not the "no
 * figure" state.
 */
export default function VolWorking({dims}) {
  const triple = toTriple(dims)
  const usable = volumetric(triple) != null
  const vol = volumetricKg(dims)
  const boxless = triple === null

  return (
    <Box
      sx={{
        display: 'grid',
        gap: GAP.xs,
        p: 1.25,
        borderRadius: '10px',
        border: `1px solid ${BORDER}`,
        bgcolor: LIGHT_BG,
      }}
    >
      <Typography sx={{...kpiLabelSx, fontSize: SIZE['2xs'], mb: 0, display: 'flex', alignItems: 'center', gap: 0.5}}>
        Volumetric weight these dimensions imply
        <Tooltip title={`Length × breadth × height in centimetres, divided by ${int(getVolumetricDivisor())} — the divisor the courier applies.`}>
          <HelpOutlineIcon sx={{fontSize: 14, color: MUTED}} />
        </Tooltip>
      </Typography>

      {usable && (
        <Box sx={{display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.25}}>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, color: SUBTLE, fontVariantNumeric: 'tabular-nums'}}>
            {fmtDims(triple)} ÷ {int(getVolumetricDivisor())} =
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.lg, fontWeight: WEIGHT.bold, color: NAVY, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2}}>
            {kg(vol)}
          </Typography>
        </Box>
      )}

      {/* No box at all. The result is a real nil, so it is stated as one. */}
      {boxless && (
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, lineHeight: 1.6}}>
          No dimensions on this material — a flyer takes the shape of whatever goes inside it. Its volumetric
          weight is {kg(vol)}, so the dead weight of the product governs what the courier bills.
        </Typography>
      )}

      {/* Something was entered, but not a triple the arithmetic can stand on. */}
      {!usable && !boxless && (
        <Box sx={{display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.25}}>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.lg, fontWeight: WEIGHT.bold, color: MUTED, lineHeight: 1.2}}>
            —
          </Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, lineHeight: 1.6}}>
            All three dimensions are needed, each above zero, before a volumetric weight can be worked out.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
